/**
 * Copy a value to the clipboard, and say whether it worked.
 *
 * **The failure path matters more than the success one here.** `navigator.clipboard` is
 * unavailable outside a secure context, so on a plain-HTTP deployment — which the
 * development stack is — every copy silently does nothing. A button that appears to work
 * and does not is worse than no button when what it was copying is a recovery code
 * shown once.
 */

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/ui';

export interface CopyButtonProps {
  /** The text to place on the clipboard. */
  value: string;
  /** What is being copied, for the accessible name. */
  label: string;
}

type Outcome = 'idle' | 'copied' | 'failed';

/**
 * Render a copy control for one value.
 *
 * @param props - The value and its label.
 * @returns The button.
 */
export function CopyButton({ value, label }: CopyButtonProps): ReactElement {
  const [outcome, setOutcome] = useState<Outcome>('idle');

  async function copy(): Promise<void> {
    try {
      if (!navigator.clipboard) throw new Error('no clipboard');
      await navigator.clipboard.writeText(value);
      setOutcome('copied');
    } catch {
      setOutcome('failed');
    }
    // Reset so the control does not read "copied" against a value the user has since
    // scrolled away from.
    window.setTimeout(() => setOutcome('idle'), 4000);
  }

  return (
    <span className="inline-flex items-center gap-2">
      <Button
        onClick={() => void copy()}
        aria-label={`Copy ${label}`}
        data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        Copy
      </Button>
      {outcome === 'copied' && (
        <span role="status" className="text-xs text-muted">
          Copied
        </span>
      )}
      {outcome === 'failed' && (
        <span role="status" className="text-xs text-warn">
          Copy unavailable — select the text and copy it manually. The clipboard needs
          HTTPS.
        </span>
      )}
    </span>
  );
}
